/**
 * Notification Channels
 *
 * Defines available notification channels and maps notification types
 * to their preference keys for the user settings system.
 */

import {
  NotificationChannel as NotificationChannels,
  type NotificationChannel,
} from "@repo/db/constants";

import type { NotificationType } from "./types";
import { NotificationTypes } from "./types";

// ============================================================================
// Notification Channels
// ============================================================================

// The database's enum, under the plural name this module has always exported.
export { type NotificationChannel, NotificationChannels };

// ============================================================================
// Preference Categories
// ============================================================================

/**
 * Categories for grouping notification preferences in the UI
 */
export const PreferenceCategories = {
  SURVEYS: "surveys",
  FINANCIAL: "financial",
  ORGANIZATION: "organization",
  STUDY_MANAGEMENT: "study_management",
} as const;

export type PreferenceCategory =
  (typeof PreferenceCategories)[keyof typeof PreferenceCategories];

// ============================================================================
// Preference Keys
// ============================================================================

/**
 * Preference keys used in user notification settings
 */
export const PreferenceKeys = {
  // Surveys
  SURVEY_SENT: "surveySent",

  // Financial
  EARNINGS_PAYOUT: "earningsPayout",
  BALANCE_ALERTS: "balanceAlerts",

  // Organization
  ORG_INVITATIONS: "orgInvitations",
  ORG_UPDATES: "orgUpdates",

  // Referral outreach (participant)
  REFERRAL_OUTREACH: "referralOutreach",

  // New opportunity alerts for referrers (participant, explicit opt-in)
  NEW_OPPORTUNITY_ALERTS: "newOpportunityAlerts",

  // Study Management (researchers)
  BUDGET_THRESHOLD: "budgetThreshold",
  STUDY_MILESTONES: "studyMilestones",
  STUDY_APPROVALS: "studyApprovals",
} as const;

export type PreferenceKey =
  (typeof PreferenceKeys)[keyof typeof PreferenceKeys];

// ============================================================================
// Notification Type to Preference Key Mapping
// ============================================================================

/**
 * Maps each notification type to its preference key
 */
export const notificationTypeToPreferenceKey: Partial<
  Record<NotificationType, PreferenceKey>
> = {
  // Organization invitations
  [NotificationTypes.ORGANIZATION_INVITATION_CREATED]:
    PreferenceKeys.ORG_INVITATIONS,
  [NotificationTypes.ORGANIZATION_INVITATION_ACCEPTED]:
    PreferenceKeys.ORG_UPDATES,
  [NotificationTypes.ORGANIZATION_INVITATION_REVOKED]:
    PreferenceKeys.ORG_UPDATES,

  // Survey/Interview invitations
  [NotificationTypes.INTERVIEW_INVITATION_SENT]: PreferenceKeys.SURVEY_SENT,

  // Financial notifications
  [NotificationTypes.INTERVIEW_EARNINGS_POSTED]: PreferenceKeys.EARNINGS_PAYOUT,
  [NotificationTypes.INTERVIEW_APPROVED]: PreferenceKeys.EARNINGS_PAYOUT,
  [NotificationTypes.INTERVIEW_AUTO_APPROVED]: PreferenceKeys.EARNINGS_PAYOUT,
  [NotificationTypes.INTERVIEW_REJECTED]: PreferenceKeys.EARNINGS_PAYOUT,
  [NotificationTypes.PAYMENT_CLAWED_BACK]: PreferenceKeys.BALANCE_ALERTS,
  [NotificationTypes.BALANCE_NEGATIVE]: PreferenceKeys.BALANCE_ALERTS,

  // Study management (researcher)
  [NotificationTypes.BUDGET_THRESHOLD_REACHED]: PreferenceKeys.BUDGET_THRESHOLD,
  [NotificationTypes.STUDY_APPROVAL_REQUESTED]: PreferenceKeys.STUDY_APPROVALS,
  [NotificationTypes.STUDY_APPROVAL_DECIDED]: PreferenceKeys.STUDY_APPROVALS,
  [NotificationTypes.STUDY_STARTED]: PreferenceKeys.STUDY_MILESTONES,

  // Panel engagement (participant)
  [NotificationTypes.INVITATION_REMINDER]: PreferenceKeys.SURVEY_SENT,
  [NotificationTypes.INVITATION_REOPENED]: PreferenceKeys.SURVEY_SENT,
  [NotificationTypes.POST_FIRST_INTERVIEW]: PreferenceKeys.SURVEY_SENT,
  [NotificationTypes.INVITATION_FEEDBACK_REQUEST]: PreferenceKeys.SURVEY_SENT,
  [NotificationTypes.POWER_USER_NPS]: PreferenceKeys.SURVEY_SENT,
  [NotificationTypes.STUDY_COMPLETED]: PreferenceKeys.SURVEY_SENT,
  // Study health alerts (researcher)
  [NotificationTypes.STUDY_STUCK]: PreferenceKeys.STUDY_MILESTONES,
  [NotificationTypes.STUDY_FULFILLMENT_ALERT]: PreferenceKeys.STUDY_MILESTONES,
  // Referral outreach (participant)
  [NotificationTypes.REFERRAL_OUTREACH_REQUEST]:
    PreferenceKeys.REFERRAL_OUTREACH,
  [NotificationTypes.POST_COMPLETION_REFERRAL]:
    PreferenceKeys.REFERRAL_OUTREACH,
  // New opportunity alerts (participant, explicit opt-in)
  [NotificationTypes.OPPORTUNITY_LISTED]: PreferenceKeys.NEW_OPPORTUNITY_ALERTS,
  // Connection-match digest (participant); shares the new-opportunity-alerts channel
  [NotificationTypes.CONNECTION_MATCH]: PreferenceKeys.NEW_OPPORTUNITY_ALERTS,
  // Multi-task studies (use the participant-facing survey preference)
  [NotificationTypes.NEXT_TASK_AVAILABLE]: PreferenceKeys.SURVEY_SENT,
  [NotificationTypes.TASK_APPROVED]: PreferenceKeys.SURVEY_SENT,
  // Turnaround notifications (researcher-facing, same preference group as study milestones)
  [NotificationTypes.STUDY_ETA_SET]: PreferenceKeys.STUDY_MILESTONES,
  [NotificationTypes.STUDY_ETA_UPDATED]: PreferenceKeys.STUDY_MILESTONES,
  // Feasibility quote ready is intentionally NOT mapped to a preference key.
  // It is an essential transactional notification (the researcher asked for a
  // price and must hear back), so it is non-configurable: a keyless type
  // resolves to [EMAIL] in resolveEnabledChannels regardless of the user's
  // stored prefs. This avoids the partial-prefs trap where a researcher with
  // SOME keys configured (but not this one) would silently get no email.
  // Booking bump (researcher-facing). Reuses the existing study-milestones key
  // on purpose: a net-new key resolves to [] for researchers with configured
  // prefs, silently dropping the email.
  [NotificationTypes.STUDY_BOOKING_NEEDED]: PreferenceKeys.STUDY_MILESTONES,
  [NotificationTypes.RESEARCHER_REVIEW_DIGEST]: PreferenceKeys.STUDY_MILESTONES,
  // INTERVIEW_BOOKED is intentionally NOT mapped, for the same reason as
  // FEASIBILITY_QUOTE_READY above: the expert owns the Google event so Google
  // invites them to nothing, and the research team is an attendee only where
  // the organization allows it, which leaves this notification as the one thing
  // that always tells both sides a real call exists at a real time. A
  // preference key - even an existing one - would let a stored `[]`, or an
  // onboarding blob that never learned the key, silently swallow it while the
  // row still records as delivered.
  // CONTRACT_SIGNED is unmapped too. It hands somebody their own copy of an
  // agreement they are a party to, which is not a message a notification
  // setting gets to withhold, and BoldSign emails the signer nothing.
  // INTERVIEW_REMINDER is unmapped for the same reason, and the cost is the
  // same tradeoff taken knowingly: two emails per booking the expert cannot
  // switch off. A net-new key would resolve to `[]` for every user whose
  // onboarding wrote the one-key {surveySent} blob, which is essentially all of
  // them, so mapping it would send nothing at all.
  // OFFICE_HOURS_ANNOUNCED is unmapped for that same reason, and it is also the
  // only carrier of the calendar invitation: withholding it would leave a
  // weekly entry on the expert's calendar with no way to update or cancel it.
  // PROJECT_OFFBOARDED is unmapped for the same reason: it is the only thing
  // that tells an expert the project they were working on has ended for them.
  // PROJECT_CLOSED is its counterpart for a project that closes with the expert
  // still on it, and is unmapped for the same reason.
};

// ============================================================================
// Available Channels per Preference Key
// ============================================================================

/**
 * User-controllable channels (excludes IN_APP which is always on)
 * IN_APP notifications are always enabled as the baseline notification feed (industry standard).
 * Users can only control external delivery channels.
 */
export type UserControllableChannel = Exclude<NotificationChannel, "IN_APP">;

/**
 * A preference key must offer at least one user-controllable channel. A key
 * with an empty list cannot be toggled in the UI, and the derived router enum
 * would reject every real channel while still accepting `[]`, so the drift is
 * invisible to a schema test. Encoded as a non-empty tuple so the type-checker
 * rejects it at the declaration below.
 */
export type AvailableChannels = [
  UserControllableChannel,
  ...UserControllableChannel[],
];

/**
 * Defines which user-controllable channels are available for each preference key
 * Note: IN_APP is always enabled and not shown in preferences UI
 */
export const availableChannelsPerPreference: Record<
  PreferenceKey,
  AvailableChannels
> = {
  // Surveys
  [PreferenceKeys.SURVEY_SENT]: [NotificationChannels.EMAIL],

  // Financial
  [PreferenceKeys.EARNINGS_PAYOUT]: [NotificationChannels.EMAIL],
  [PreferenceKeys.BALANCE_ALERTS]: [NotificationChannels.EMAIL],

  // Referral outreach
  [PreferenceKeys.REFERRAL_OUTREACH]: [NotificationChannels.EMAIL],

  // New opportunity alerts - email only (digest-style updates)
  [PreferenceKeys.NEW_OPPORTUNITY_ALERTS]: [NotificationChannels.EMAIL],

  // Organization - email only (researcher-facing, web-only)
  [PreferenceKeys.ORG_INVITATIONS]: [NotificationChannels.EMAIL],
  [PreferenceKeys.ORG_UPDATES]: [NotificationChannels.EMAIL],

  // Study management
  [PreferenceKeys.BUDGET_THRESHOLD]: [NotificationChannels.EMAIL],
  [PreferenceKeys.STUDY_MILESTONES]: [NotificationChannels.EMAIL],
  [PreferenceKeys.STUDY_APPROVALS]: [NotificationChannels.EMAIL],
};

// ============================================================================
// Preference Category Mapping
// ============================================================================

/**
 * Maps preference keys to their category
 */
export const preferenceKeyToCategory: Record<
  PreferenceKey,
  PreferenceCategory
> = {
  [PreferenceKeys.SURVEY_SENT]: PreferenceCategories.SURVEYS,
  [PreferenceKeys.REFERRAL_OUTREACH]: PreferenceCategories.SURVEYS,
  [PreferenceKeys.NEW_OPPORTUNITY_ALERTS]: PreferenceCategories.SURVEYS,
  [PreferenceKeys.EARNINGS_PAYOUT]: PreferenceCategories.FINANCIAL,
  [PreferenceKeys.BALANCE_ALERTS]: PreferenceCategories.FINANCIAL,
  [PreferenceKeys.ORG_INVITATIONS]: PreferenceCategories.ORGANIZATION,
  [PreferenceKeys.ORG_UPDATES]: PreferenceCategories.ORGANIZATION,
  [PreferenceKeys.BUDGET_THRESHOLD]: PreferenceCategories.STUDY_MANAGEMENT,
  [PreferenceKeys.STUDY_MILESTONES]: PreferenceCategories.STUDY_MANAGEMENT,
  [PreferenceKeys.STUDY_APPROVALS]: PreferenceCategories.STUDY_MANAGEMENT,
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the preference key for a notification type
 */
export function getPreferenceKeyForType(
  type: NotificationType,
): PreferenceKey | undefined {
  return notificationTypeToPreferenceKey[type];
}

/**
 * Get available user-controllable channels for a notification type
 * Note: IN_APP is always enabled and not included in this list
 */
export function getAvailableChannelsForType(
  type: NotificationType,
): UserControllableChannel[] {
  const preferenceKey = getPreferenceKeyForType(type);
  if (!preferenceKey) {
    return [];
  } // No external channels by default
  return availableChannelsPerPreference[preferenceKey];
}

/**
 * Check if a channel is available for a notification type
 * Note: IN_APP is always available (returns true)
 */
export function isChannelAvailableForType(
  type: NotificationType,
  channel: NotificationChannel,
): boolean {
  // IN_APP is always available
  if (channel === NotificationChannels.IN_APP) {
    return true;
  }
  const availableChannels = getAvailableChannelsForType(type);
  return availableChannels.includes(channel);
}

/**
 * Get all preference keys for a category
 */
export function getPreferenceKeysForCategory(
  category: PreferenceCategory,
): PreferenceKey[] {
  return (Object.keys(preferenceKeyToCategory) as PreferenceKey[]).filter(
    (key) => preferenceKeyToCategory[key] === category,
  );
}
